import { Button } from "@/components/ui/button";
import { Eye, ExternalLink, Save, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfigurationActionsProps {
  onSave: () => void;
  onTest: () => void;
  saving: boolean;
  canTest: boolean;
}

export function ConfigurationActions({ onSave, onTest, saving, canTest }: ConfigurationActionsProps) {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  
  return (
    <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-6 space-y-4">
      {/* Instructions */}
      <div className="text-center space-y-2">
        <h4 className="font-medium text-slate-900">Ready to activate your chatbot?</h4>
        <p className="text-sm text-slate-600">
          Save your configuration first, then test how your chatbot works before going live.
        </p>
      </div>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 px-8 py-3 shadow-md hover:shadow-lg transition-all duration-200 order-1 sm:order-2"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>

        <Button
          variant="outline"
          onClick={onTest}
          disabled={!canTest}
          className="flex items-center gap-2 border-slate-300 bg-white hover:bg-slate-50 px-6 py-3 shadow-sm hover:shadow-md transition-all duration-200 order-2 sm:order-1"
        >
          <Eye className="w-4 h-4" />
          Test Chatbot
          <ExternalLink className="w-3 h-3" />
        </Button>
      </div>

      {/* Status Message */}
      {!canTest && (
        <div className="text-center">
          <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
            💡 Enable your chatbot and save configuration to unlock testing
          </p>
        </div>
      )}
    </div>
  );
} 